import React from 'react';
import { Search, Monitor, Settings } from 'lucide-react';
import { SearchResultsTable } from '../tables/SearchResultsTable';
import { EmptyState } from './EmptyState';
import type { Product } from '../../pages/CatalogPage';
import type { Country, Tab, Level2PlatformData } from '../../types';

type SearchResultsSectionProps = {
  hardwareProducts: Product[];
  softwareProducts: Product[];
  level2SearchResults: Record<string, Level2PlatformData>;
  visibleCountries: Country[];
  browseMode: boolean;
  selectedCountry: string | null;
  highlightedProduct: string | null;
  tabs: Tab[];
  tabSearchQuery: string;
  onDeviceClick: (product: Product) => void;
  onProductClick: (product: Product) => void;
};

export function SearchResultsSection({
  hardwareProducts,
  softwareProducts,
  level2SearchResults,
  visibleCountries,
  browseMode,
  selectedCountry,
  highlightedProduct,
  tabs,
  tabSearchQuery,
  onDeviceClick,
  onProductClick,
}: SearchResultsSectionProps) {
  const hasLevel2Results = Object.keys(level2SearchResults).length > 0;
  const hasResults = hardwareProducts.length > 0 || softwareProducts.length > 0 || hasLevel2Results;

  if (!hasResults) {
    return (
      <EmptyState
        icon={Search}
        message={`No results found for "${tabSearchQuery}"`}
        subMessage="Try searching with different keywords"
      />
    );
  }

  const handleProductClick = (product: Product, type: string) => {
    if (type === 'hardware') {
      onDeviceClick(product);
    } else {
      onProductClick(product);
    }
  };

  return (
    <div className="space-y-6">
      {hardwareProducts.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Monitor className="w-5 h-5" />
            {tabs.find(tab => tab.id === 'supported-hardware')?.label || 'Hardware'} Results (
            {hardwareProducts.length})
          </h3>
          <SearchResultsTable
            products={hardwareProducts}
            visibleCountries={visibleCountries}
            type="hardware"
            browseMode={browseMode}
            selectedCountry={selectedCountry}
            highlightedProduct={highlightedProduct}
            onProductClick={handleProductClick}
          />
        </div>
      )}

      {softwareProducts.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {tabs.find(tab => tab.id === 'product-modules')?.label || 'Software'} Results (
            {softwareProducts.length})
          </h3>
          <SearchResultsTable
            products={softwareProducts}
            visibleCountries={visibleCountries}
            type="software"
            browseMode={browseMode}
            selectedCountry={selectedCountry}
            highlightedProduct={highlightedProduct}
            onProductClick={handleProductClick}
          />
        </div>
      )}

      {hasLevel2Results &&
        Object.entries(level2SearchResults).map(([platformName, platformData]) => {
          const displayName =
            platformName === 'Marketplace Integrations' ? 'Marketplace' : platformName;
          return (
            <div key={platformName}>
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                {displayName} Results ({platformData.products.length})
              </h3>
              <SearchResultsTable
                products={platformData.products}
                visibleCountries={visibleCountries}
                type="level2"
                browseMode={browseMode}
                selectedCountry={selectedCountry}
                highlightedProduct={highlightedProduct}
                onProductClick={handleProductClick}
              />
            </div>
          );
        })}
    </div>
  );
};
